import React, { useEffect, useRef, useState } from "react";

const ImageCarousel = ({ images = [] }) => {
  const photos = Array.isArray(images) ? images : [];
  const viewport = useRef(null);
  const gesture = useRef(null);
  const pendingIndex = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [offset, setOffset] = useState(0);
  const [settling, setSettling] = useState(false);
  const [dragging, setDragging] = useState(false);
  const index = Math.min(selectedIndex, Math.max(0, photos.length - 1));

  const finishAnimation = () => {
    if (pendingIndex.current !== null) setSelectedIndex(pendingIndex.current);
    pendingIndex.current = null;
    setSettling(false);
    setOffset(0);
  };

  useEffect(() => {
    setSelectedIndex(0); setOffset(0); setSettling(false); setDragging(false);
    gesture.current = null; pendingIndex.current = null;
  }, [images]);

  useEffect(() => {
    if (!settling) return;
    // Also complete if a browser suppresses transitionend (e.g. a hidden tab).
    const timer = setTimeout(finishAnimation, 350);
    return () => clearTimeout(timer);
  }, [settling]);

  const release = (event, cancelled = false) => {
    const start = gesture.current;
    if (!start || start.id !== event.pointerId) return;
    gesture.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const dx = Math.max(-start.width, Math.min(start.width, event.clientX - start.x));
    const advance = !cancelled && start.direction === 'horizontal' && Math.abs(dx) > start.width / 2;
    pendingIndex.current = advance ? (index + (dx < 0 ? 1 : -1) + photos.length) % photos.length : index;
    setOffset(advance ? (dx < 0 ? -start.width : start.width) : 0);
    setSettling(true);
  };

  if (!photos.length) return <p className="p-3 text-center text-sm text-slate-500">No product photos available.</p>;
  // Neighbor copies keep wrapping seamless without moving through every photo.
  const slides = photos.length > 1 ? [(index - 1 + photos.length) % photos.length, index, (index + 1) % photos.length] : [index];

  return (
    <div className="min-w-0 text-center" role="region" aria-label="Product photos" aria-roledescription="carousel">
      <div ref={viewport} className="overflow-hidden rounded shadow-sm select-none"
        style={{ touchAction: 'pan-y pinch-zoom', cursor: photos.length > 1 ? (dragging ? 'grabbing' : 'grab') : 'auto' }}
        onDragStart={event => event.preventDefault()}
        onPointerDown={event => {
          if (event.isPrimary === false) {
            if (gesture.current) release({ ...event, pointerId: gesture.current.id, currentTarget: event.currentTarget }, true);
            return;
          }
          if (photos.length < 2 || settling || (event.pointerType === 'mouse' && event.button !== 0)) return;
          const width = viewport.current.getBoundingClientRect().width;
          if (!width) return;
          gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, width, direction: null };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={event => {
          const start = gesture.current;
          if (!start || start.id !== event.pointerId) return;
          const dx = event.clientX - start.x, dy = event.clientY - start.y;
          if (!start.direction && Math.max(Math.abs(dx), Math.abs(dy)) > 6) {
            start.direction = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
          }
          if (start.direction !== 'horizontal') return;
          setDragging(true);
          setOffset(Math.max(-start.width, Math.min(start.width, dx)));
        }}
        onPointerUp={event => release(event)}
        onPointerCancel={event => release(event, true)}
        onLostPointerCapture={event => release(event, true)}
      >
        <div className="flex items-start motion-reduce:!transition-none"
          style={{ transform: `translate3d(calc(${photos.length > 1 ? '-100%' : '0%'} + ${offset}px), 0, 0)`, transition: settling ? 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none' }}
          onTransitionEnd={event => { if (event.target === event.currentTarget && event.propertyName === 'transform') finishAnimation(); }}>
          {slides.map((photoIndex, position) => (
            <img key={position} src={photos[photoIndex]} alt={photoIndex === index && position === (slides.length === 1 ? 0 : 1) ? `Product ${index + 1}` : ''}
              aria-hidden={position !== (slides.length === 1 ? 0 : 1) ? true : undefined}
              draggable={false} className="block w-full min-w-0 shrink-0 object-contain" />
          ))}
        </div>
      </div>
      {photos.length > 1 && <div className="mt-1 flex flex-wrap items-center justify-center">
        {photos.map((_, photoIndex) => (
          <button key={photoIndex} type="button" aria-label={`Show photo ${photoIndex + 1}`}
            aria-current={index === photoIndex ? 'true' : undefined}
            className="inline-flex h-11 w-11 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-600"
            onClick={() => { gesture.current = null; pendingIndex.current = null; setSettling(false); setDragging(false); setOffset(0); setSelectedIndex(photoIndex); }}>
            <span className={`h-2.5 w-2.5 rounded-full ${index === photoIndex ? 'bg-sky-600' : 'bg-slate-300'}`} />
          </button>
        ))}
      </div>}
      <span className="sr-only" aria-live="polite">Photo {index + 1} of {photos.length}</span>
    </div>
  );
};

export default ImageCarousel;
